import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Accordion, Badge, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { groupCreate, groupsGetAll, groupUpdate, personGroupUpdate, personsGroupsSummaryGet } from '../api/index.js'

import { Button } from '../common/Button.tsx'
import { IconAddNew2 } from '../common/icons.jsx'
import Switch from '../common/Switch.jsx'

const emptyGroup = {
	id: null,
	name: '',
	colour: '#003a78',
	code: '',
	active: true,
}

export function EmployeeGroupsView() {
	const { t } = useTranslation()
	const gridRef = useRef()

	const [groups, setGroups] = useState([{ ...emptyGroup }])
	const [personRows, setPersonRows] = useState([])
	const [showValidation, setShowValidation] = useState(false)

	const mapGroupToApi = (group) => ({
		id: group.id || 0,
		groupCode: group.code,
		name: group.name,
		active: group.active,
		colour: group.colour,
		startDate: new Date().toISOString().slice(0, 10),
	})

	const loadGroups = useCallback(async () => {
		try {
			const res = await groupsGetAll()
			const data = res.data || []

			const mapped = data.map((g) => ({
				id: g.id,
				name: g.name,
				code: g.groupCode,
				colour: g.colour || '#003a78',
				active: g.active,
			}))

			setGroups(mapped.length ? mapped : [emptyGroup])
		} catch (error) {
			console.error('Virhe ryhmien latauksessa:', error)
		}
	}, [])

	const loadSummary = useCallback(async () => {
		try {
			const res = await personsGroupsSummaryGet()
			const data = res.data || []

			setPersonRows(
				data.map((r) => ({
					personId: r.personId,
					fullName: r.personName,
					selectedGroupId: r.employeeGroupId || null,
				})),
			)
		} catch (error) {
			console.error('Virhe ryhmäyhteenvedon latauksessa:', error)
		}
	}, [])

	useEffect(() => {
		loadGroups()
		loadSummary()
	}, [loadGroups, loadSummary])

	const saveGroups = async () => {
		const validGroups = groups.filter((g) => g.name?.trim() || g.code?.trim())

		try {
			for (const group of validGroups) {
				if (!group.name?.trim() || !group.code?.trim()) continue

				const payload = mapGroupToApi(group)

				if (group.id) {
					await groupUpdate(group.id, payload)
				} else {
					await groupCreate(payload)
				}
			}

			await loadGroups()
			await loadSummary()
		} catch (error) {
			console.error('Virhe ryhmien tallennuksessa:', error)
		}
	}

	const addRow = () => setGroups([...groups, { ...emptyGroup }])

	const updateGroup = (index, field, value) => {
		setGroups((prevGroups) =>
			prevGroups.map((group, i) => {
				if (i === index) {
					return { ...group, [field]: value }
				}
				return group
			}),
		)
	}

	const onCellValueChanged = async (params) => {
		if (params.colDef.field !== 'selectedGroupId') return

		const personId = params.data.personId
		let newGroupId = params.data.selectedGroupId
		const oldGroupId = params.oldValue

		if (newGroupId === 'CLEAR_VALUE') {
			newGroupId = null
			params.node.setDataValue('selectedGroupId', null)
		}

		if (newGroupId === oldGroupId) return

		try {
			const assignmentPayload = {
				employeeGroupId: newGroupId,
				startDate: new Date().toISOString().slice(0, 10),
				endDate: null,
			}

			await personGroupUpdate(personId, assignmentPayload)
		} catch (error) {
			console.error('Virhe henkilön ryhmän päivityksessä:', error)
			await loadSummary()
		}
	}

	const columnDefs = [
		{
			headerName: t('Person', 'Henkilö'),
			field: 'fullName',
			sortable: true,
			width: 260,
		},
		{
			headerName: t('EmployeeGroup', 'Työntekijäryhmä'),
			field: 'selectedGroupId',
			editable: true,
			singleClickEdit: true,
			width: 360,
			cellEditor: 'agRichSelectCellEditor',
			cellEditorParams: (params) => {
				const availableGroups = groups.filter((g) => g.id).map((g) => g.id)
				const currentSelection = params.data.selectedGroupId

				const values = currentSelection ? ['CLEAR_VALUE', ...availableGroups] : availableGroups

				return {
					values: values,
					multiSelect: false,
					formatValue: (value) => {
						if (value === 'CLEAR_VALUE') return t('ClearSelection', 'Tyhjennä valinta')
						return groups.find((g) => g.id === value)?.name || value
					},
					cellRenderer: (itemParams) => {
						const value = itemParams.value
						if (value === 'CLEAR_VALUE') {
							return (
								<span style={{ opacity: 0.5, fontStyle: 'italic', color: '#6c757d' }}>
									{t('ClearSelection', 'Tyhjennä valinta')}
								</span>
							)
						}

						const group = groups.find((g) => g.id === value)
						return group ? group.name : value
					},
				}
			},
			cellRenderer: (params) => {
				const groupId = params.value
				if (!groupId || groupId === 'CLEAR_VALUE') {
					return <span style={{ color: '#888' }}>{t('SelectGroup', 'Valitse ryhmä')}</span>
				}

				const group = groups.find((g) => g.id === groupId)
				if (!group) return null

				const bgColor = group.active ? group.colour : '#888'

				return (
					<Badge
						bg={bgColor}
						style={{
							backgroundColor: bgColor,
							color: '#fff',
							marginRight: 4,
						}}
					>
						{group.name}
					</Badge>
				)
			},
		},
	]

	return (
		<div className="d-flex flex-column" style={{ height: '100%', background: '#FFF' }}>
			<Accordion defaultActiveKey="groups">
				<Accordion.Item eventKey="groups">
					<Accordion.Header>{t('EmployeeGroupManagement', 'Työntekijäryhmien hallinta')}</Accordion.Header>
					<Accordion.Body>
						{groups.map((group, index) => (
							<Row key={group.id || `new-group-${index}`} className="align-items-center mb-2">
								<Col className="mt-4" style={{ flex: '0 0 50px', maxWidth: 50 }}>
									<Form.Control
										type="color"
										value={group.colour}
										onChange={(e) => updateGroup(index, 'colour', e.target.value)}
										style={{ width: 36, height: 36, padding: 0 }}
									/>
								</Col>

								<Col md={6}>
									<Form.Label
										style={{
											position: 'relative',
											left: '.8rem',
											top: '.4rem',
											zIndex: 1,
											margin: 0,
											background: 'var(--almost-white)',
											color: 'var(--label-color)',
											fontSize: '11px',
										}}
									>
										{t('Name', 'Nimi')}
									</Form.Label>
									<Form.Control
										value={group.name}
										onChange={(e) => updateGroup(index, 'name', e.target.value)}
										isInvalid={showValidation && !group.name?.trim()}
									/>
									{showValidation && !group.name?.trim() && (
										<div className="text-danger" style={{ fontSize: '0.8rem' }}>
											{t('NameIsMandatory', 'Nimi on pakollinen')}
										</div>
									)}
								</Col>

								<Col md={3}>
									<Form.Label
										style={{
											position: 'relative',
											left: '.8rem',
											top: '.4rem',
											zIndex: 1,
											margin: 0,
											background: 'var(--almost-white)',
											color: 'var(--label-color)',
											fontSize: '11px',
										}}
									>
										{t('Code', 'Tunniste')}
									</Form.Label>
									<Form.Control
										value={group.code}
										onChange={(e) => updateGroup(index, 'code', e.target.value)}
										isInvalid={showValidation && !group.code?.trim()}
									/>
									{showValidation && !group.code?.trim() && (
										<div className="text-danger" style={{ fontSize: '0.8rem' }}>
											{t('CodeIsMandatory', 'Tunnus on pakollinen')}
										</div>
									)}
								</Col>

								<Col md={1} className="d-flex align-items-center mt-3">
									<Switch
										className="d-flex align-items-center"
										checked={group.active}
										onChange={(e) => updateGroup(index, 'active', e.target.checked)}
									/>
									<span
										style={{
											marginLeft: '6px',
											fontSize: '0.8rem',
											whiteSpace: 'nowrap',
										}}
									>
										{group.active ? t('Active', 'Aktiivinen') : t('Activate', 'Aktivoi')}
									</span>
								</Col>
							</Row>
						))}

						<div className="d-flex mt-4">
							<Button variant="outline-primary" className="d-flex align-items-center gap-2 me-auto" onClick={addRow}>
								<IconAddNew2 />
								{t('AddGroup', 'Lisää uusi ryhmä')}
							</Button>
							<Button
								className="me-2"
								onClick={() => {
									const validGroups = groups.filter((g) => g.name?.trim() || g.code?.trim())

									if (validGroups.some((g) => !g.name?.trim() || !g.code?.trim())) {
										setShowValidation(true)
										return
									}

									setShowValidation(false)
									saveGroups()
								}}
							>
								{t('Save', 'Tallenna')}
							</Button>
						</div>
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>

			<div className="mt-4 flex-grow-1 d-flex flex-column">
				<div className="ag-theme-material flex-grow-1" style={{ height: '400px', width: '100%' }}>
					<AgGridReact
						ref={gridRef}
						rowData={personRows}
						columnDefs={columnDefs}
						animateRows
						suppressCellFocus
						onCellValueChanged={onCellValueChanged}
						stopEditingWhenCellsLoseFocus
						getRowId={(p) => p.data.personId.toString()}
					/>
				</div>
			</div>
		</div>
	)
}
